import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Quote, Send, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, onRatingChange, readonly = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onRatingChange && onRatingChange(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform ${!readonly && 'hover:scale-110'}`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating)
                ? 'fill-[#C6A87C] text-[#C6A87C]'
                : 'fill-transparent text-[#E5E0D6]'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review }) => {
  const { language } = useLanguage();
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="bg-white p-6 rounded-sm card-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-[#1A1A1A]">{review.guest_name}</h4>
          {review.stay_date && (
            <p className="text-xs text-[#5A5A5A]">
              {language === 'es' ? 'Se hospedó en' : 'Stayed in'} {formatDate(review.stay_date)}
            </p>
          )}
        </div>
        <StarRating rating={review.rating} readonly size="sm" />
      </div>
      <div className="relative">
        <Quote className="absolute -top-1 -left-1 w-6 h-6 text-[#E5E0D6]" />
        <p className="text-[#5A5A5A] text-sm leading-relaxed pl-6">
          {review.comment}
        </p>
      </div>
    </div>
  );
};

// Reviews Section Component
export const ReviewsSection = ({ cabinId = null, showForm = true }) => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    guest_name: '',
    rating: 5,
    comment: '',
    stay_date: ''
  });

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [cabinId]);

  const fetchReviews = async () => {
    try {
      const url = cabinId ? `${API}/reviews?cabin_id=${cabinId}` : `${API}/reviews`;
      const response = await axios.get(url);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.guest_name || !formData.comment || !formData.rating) {
      toast.error(language === 'es' ? 'Por favor completa todos los campos requeridos' : 'Please complete all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        cabin_id: cabinId,
        guest_name: formData.guest_name,
        rating: formData.rating,
        comment: formData.comment,
        stay_date: formData.stay_date || null
      });

      setSubmitted(true);
      toast.success(t('reviews.thankYou'));
      fetchReviews();
      fetchStats();
      
      setTimeout(() => {
        setIsDialogOpen(false);
        setSubmitted(false);
        setFormData({ guest_name: '', rating: 5, comment: '', stay_date: '' });
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(language === 'es' ? 'Error al enviar la reseña' : 'Error submitting review');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate month options for last 12 months
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  return (
    <section data-testid="reviews-section" className="py-16 md:py-24 bg-[#F5F2EB]">
      <div className="container-ribba">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
            {t('reviews.subtitle')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] font-bold mt-3 mb-4">
            {t('reviews.title')}
          </h2>
          
          {/* Stats */}
          {stats.total_reviews > 0 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <div className="flex items-center gap-2">
                <span className="text-4xl font-heading font-bold text-[#2F3E30]">
                  {stats.average_rating}
                </span>
                <div>
                  <StarRating rating={Math.round(stats.average_rating)} readonly size="sm" />
                  <p className="text-xs text-[#5A5A5A] mt-1">
                    {stats.total_reviews} {stats.total_reviews !== 1 ? t('reviews.reviewsText') : (language === 'es' ? 'reseña' : 'review')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {reviews.slice(0, 6).map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-sm mb-12">
            <p className="text-[#5A5A5A]">
              {language === 'es' 
                ? 'Aún no hay reseñas. ¡Sé el primero en compartir tu experiencia!'
                : "No reviews yet. Be the first to share your experience!"}
            </p>
          </div>
        )}

        {/* Add Review Button */}
        {showForm && (
          <div className="text-center">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="add-review-btn"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Star className="w-5 h-5" />
                  {t('reviews.leaveReview')}
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">
                    {language === 'es' ? 'Comparte tu experiencia' : 'Share your experience'}
                  </DialogTitle>
                </DialogHeader>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#3D6F48]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-[#3D6F48]" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                      {t('reviews.thankYou')}
                    </h3>
                    <p className="text-[#5A5A5A] text-sm">
                      {language === 'es' ? 'Tu opinión nos ayuda a mejorar.' : 'Your feedback helps us improve.'}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                    {/* Rating */}
                    <div>
                      <Label className="text-sm font-medium text-[#5A5A5A] mb-3 block">
                        {language === 'es' ? 'Tu calificación' : 'Your rating'} *
                      </Label>
                      <div className="flex justify-center">
                        <StarRating
                          rating={formData.rating}
                          onRatingChange={(r) => setFormData({ ...formData, rating: r })}
                          size="lg"
                        />
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <Label htmlFor="review-name" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {t('reviews.yourName')} *
                      </Label>
                      <Input
                        id="review-name"
                        data-testid="review-name-input"
                        value={formData.guest_name}
                        onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                        placeholder={language === 'es' ? '¿Cómo te llamas?' : "What's your name?"}
                        required
                      />
                    </div>

                    {/* Stay date */}
                    <div>
                      <Label className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {language === 'es' ? '¿Cuándo te hospedaste?' : 'When did you stay?'} ({t('common.optional')})
                      </Label>
                      <Select
                        value={formData.stay_date}
                        onValueChange={(value) => setFormData({ ...formData, stay_date: value })}
                      >
                        <SelectTrigger data-testid="review-date-select">
                          <SelectValue placeholder={language === 'es' ? 'Selecciona el mes' : 'Select month'} />
                        </SelectTrigger>
                        <SelectContent>
                          {getMonthOptions().map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Comment */}
                    <div>
                      <Label htmlFor="review-comment" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                        {language === 'es' ? 'Tu comentario' : 'Your comment'} *
                      </Label>
                      <Textarea
                        id="review-comment"
                        data-testid="review-comment-input"
                        value={formData.comment}
                        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                        placeholder={language === 'es' 
                          ? 'Cuéntanos sobre tu experiencia en las cabañas...' 
                          : 'Tell us about your experience at the cabins...'}
                        rows={4}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="submit-review-btn"
                      disabled={submitting}
                      className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          {t('reviews.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {t('reviews.submit')}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
